import { Link } from "react-router";
import {
  ArrowLeft,
  Cloud,
  RefreshCcw,
  Activity,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatRelative } from "@/lib/utils";
import { HubSpotObjectLinkingTable } from "./_components/hubspot-object-linking-table";
import { RePullPartnersDialog } from "./_components/re-pull-partners-dialog";
import { PartnerFieldMappingCard } from "./_components/partner-field-mapping-card";
import { useIntegrationsStatus, type DealSyncHealth } from "./use-integrations-status";

// Phase 7 sub-bucket 18 — SPA port of
// /app/admin/settings/integrations/hubspot/page.tsx. Three sections:
//   1. Storage object  — read-only HUBSPOT_PARTNER_OBJECT_TYPE_ID value
//   2. Discover        — partner snapshot count + "Re-pull now" CTA
//   3. Object Linking  — vendor-editable typeId → attribution name table

export function IntegrationsHubspotPage() {
  const statusQuery = useIntegrationsStatus();
  const hubspot = statusQuery.data?.hubspot;
  const partnerObjectTypeId = hubspot?.partnerObjectTypeId ?? "(unset)";
  const portalId = hubspot?.portalId ?? "(unset)";
  const partnersCount = hubspot?.partnersCount ?? 0;
  const lastUpdatedAt = hubspot?.lastPartnerUpdatedAt ?? null;
  const sync = hubspot?.sync ?? null;

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            HubSpot
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            How Colab reads from and writes to your HubSpot portal.
          </p>
        </div>
        <Link
          to="/admin/settings/integrations"
          className="inline-flex items-center gap-1 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to integrations
        </Link>
      </header>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-display tracking-tight">
              Storage object
            </CardTitle>
            <CardDescription>
              The HubSpot object that holds your partner records. Set via
              environment variables and locked to prevent accidental
              re-targeting.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-3 sm:grid-cols-2">
              <Field label="Portal ID">
                <code className="rounded bg-muted px-1.5 py-0.5 text-[11px]">
                  {portalId}
                </code>
              </Field>
              <Field label="Partner object typeId">
                <code className="rounded bg-muted px-1.5 py-0.5 text-[11px]">
                  {partnerObjectTypeId}
                </code>
              </Field>
            </dl>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Change in <code className="rounded bg-muted px-1 py-0.5">.env.local</code>{" "}
              or the Cloudflare deploy secrets — <code>HUBSPOT_PORTAL_ID</code>{" "}
              and <code>HUBSPOT_PARTNER_OBJECT_TYPE_ID</code>.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-display tracking-tight">
              Discover
            </CardTitle>
            <CardDescription>
              Partners currently tracked in Colab with a HubSpot record link.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between gap-4">
              <div>
                {statusQuery.isLoading ? (
                  <Skeleton className="h-9 w-20" />
                ) : (
                  <p className="text-3xl font-display tracking-tight tabular-nums">
                    {partnersCount}
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground">
                  partners with HubSpot record IDs
                </p>
                {lastUpdatedAt && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Latest record updated{" "}
                    {new Date(lastUpdatedAt).toLocaleDateString("en-IE")}
                  </p>
                )}
              </div>
              <RePullPartnersDialog partnerCount={partnersCount} />
            </div>
            <p className="mt-3 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <RefreshCcw className="h-3 w-3" />
              Re-pulls hit HubSpot via the service-role admin client and may
              take 30-60 seconds for ~100 partners.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-display tracking-tight">
              Sync health
            </CardTitle>
            <CardDescription>
              The deal/attribution sync runs every 15 minutes and on each
              HubSpot deal webhook. This reflects the most recent run from
              either source.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {statusQuery.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <SyncHealth sync={sync} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-display tracking-tight">
              Object linking
            </CardTitle>
            <CardDescription>
              Map HubSpot association typeIds to Colab attribution names. The
              backfill script reads from this table on next run.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <HubSpotObjectLinkingTable />
            <p className="mt-3 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Cloud className="h-3 w-3" />
              Adding a new association type is deferred to Session 10. To wire
              one up today, insert a row directly into{" "}
              <code className="rounded bg-muted px-1 py-0.5">
                hubspot_object_linking
              </code>{" "}
              and extend the relationship enum in{" "}
              <code className="rounded bg-muted px-1 py-0.5">domain.ts</code>.
            </p>
          </CardContent>
        </Card>

        <PartnerFieldMappingCard />
      </div>
    </div>
  );
}

function SyncHealth({ sync }: { sync: DealSyncHealth | null }) {
  if (!sync || sync.lastRunAt === null) {
    return (
      <p className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        No sync recorded yet — the cron may not be running in this environment.
      </p>
    );
  }

  const failed = sync.lastRunOk === false;
  const inFlight = sync.lastRunOk === null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {failed ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive">
            <AlertTriangle className="h-3.5 w-3.5" />
            Last sync failed
          </span>
        ) : inFlight ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
            <Activity className="h-3.5 w-3.5" />
            Sync in progress
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Healthy
          </span>
        )}
        <span className="text-sm text-muted-foreground">
          Last run {formatRelative(sync.lastRunAt)}
          {sync.lastRunRows !== null && !failed
            ? ` · ${sync.lastRunRows} rows`
            : ""}
        </span>
      </div>
      <dl className="grid gap-3 sm:grid-cols-2">
        <Field label="Deals last refreshed">
          <span className="text-sm tabular-nums">
            {formatRelative(sync.dealsLastPulledAt)}
          </span>
        </Field>
        <Field label="Failed runs (7 days)">
          <span
            className={
              sync.recentErrorCount > 0
                ? "text-sm font-medium tabular-nums text-destructive"
                : "text-sm tabular-nums"
            }
          >
            {sync.recentErrorCount}
          </span>
        </Field>
      </dl>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-foreground">{children}</dd>
    </div>
  );
}
